import { ReactNode, forwardRef, cloneElement, isValidElement, ReactElement, useState, useEffect } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface TooltipWrapperProps {
  children: ReactNode;
  title: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  delayDuration?: number;
}

/**
 * TooltipWrapper - Hover tooltip for desktop, instant touch tooltip for mobile
 * 
 * On mobile (viewport < 768px):
 * - delayDuration is set to 0 so the tooltip appears immediately on touch
 * - touch-manipulation is added to the trigger to prevent double-tap zoom
 * - Tooltip content is larger and more readable
 * 
 * Use when icon/button already signals interaction:
 * - Icon-only action buttons (edit, delete, view)
 * - Tab clarifications
 * - Status badges
 * 
 * Default: bottom position, 300ms delay
 * 
 * @see docs/UI_TOOLTIP_GUIDELINES.md for usage guide
 */
export const TooltipWrapper = forwardRef<HTMLElement, TooltipWrapperProps>(
  function TooltipWrapper({ children, title, side = 'bottom', delayDuration = 300 }, ref) {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const checkMobile = () => setIsMobile(window.innerWidth < 768);
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Clone child with forwarded ref and mobile touch-manipulation class
    const childWithRef = isValidElement(children)
      ? cloneElement(
          children as ReactElement<{ ref?: React.Ref<HTMLElement>; className?: string; style?: React.CSSProperties }>,
          {
            ref,
            className: cn(
              (children as ReactElement).props.className,
              isMobile && 'touch-manipulation'
            ),
            style: {
              ...(children as ReactElement).props.style,
              ...(isMobile ? { touchAction: 'manipulation' } : {})
            }
          }
        )
      : children;

    return (
      <TooltipProvider delayDuration={isMobile ? 0 : delayDuration}>
        <Tooltip>
          <TooltipTrigger asChild>
            {childWithRef}
          </TooltipTrigger>
          <TooltipContent
            side={side}
            className={cn(
              'bg-popover text-popover-foreground border-border',
              isMobile && 'text-base p-4 max-w-[300px] z-[80]'
            )}
          >
            <p className={cn('text-sm', isMobile && 'text-base leading-relaxed')}>{title}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
);
